package top.wcpe.allincore.dsl.ir.expr

import kotlin.jvm.JvmField
import kotlin.jvm.JvmStatic

/**
 * 表达式字节流解析器，与 `dsl-compiler` 的 `ExprSerializer` 严格对偶。
 *
 * 字节格式（大端序）：
 * ```
 * ExprNode:
 *   tag(u8) | payload
 *     1 String   : LP-UTF8
 *     2 Number   : 8B IEEE 754 double（大端）
 *     3 Bool     : 1B
 *     4 StateRef : LP-UTF8 name
 *    34 params引用: LP-UTF8 name
 *     5 (已退役)
 *     6 Interp   : u16 partCount, { partKind(u8)[0=Text,1=Expr] + payload }*
 *     7 BinaryOp : LP-UTF8 op + Expr(left) + Expr(right)
 *     8 MemberAccess : Expr(target) + LP-UTF8 member
 *     9 IdentRef     : LP-UTF8 name
 *    10 ThemeRef     : LP-UTF8 key
 *    11 MemberCall   : Expr(target) + LP-UTF8 method + u16 argCount + { Expr(arg) }*
 *    12 NullLit      : ∅
 *    13 UnaryOp      : op(u8)[0=NEG,1=NOT] + Expr(operand)
 *    14 SafeMemberAccess : Expr(target) + LP-UTF8 member
 *    15 SafeMemberCall   : Expr(target) + LP-UTF8 method + u16 argCount + { Expr(arg) }*
 *    16 Elvis            : Expr(left) + Expr(right)
 *    17 Ternary          : Expr(cond) + Expr(then) + Expr(else)
 *    18 LocalRef         : LP-UTF8 name
 *    27 IfExpr           : Expr(cond) + then StmtList + else StmtList
 *
 * StmtNode:
 *   tag(u8) | payload
 *     1 Assign   : lvalueKind(u8)[0=StateLValue,1=LocalLValue] + LP-UTF8 name + Expr(rhs)
 *     2 Request  : LP-UTF8 namespace + LP-UTF8 handler
 *                  + u16 payloadCount, { LP-UTF8 key + Expr value }*
 *                  + u8 callbackCount(0-4), { Callback }*
 *    14 NetRequest : Request payload shape, decoded with netMode=true and timeoutMs=null
 *    15 NetRequestTimeout : NetRequest payload shape + Expr(timeoutMs)
 *
 *  -- D2-a 预留（IR v8）：状态机 wire format --
 *    16 StateMachineHeader : u16 stateCount + LP-UTF8 entryStateName
 *                            + u16 varCount, { LP-UTF8 name + u8 IrType ordinal }*
 *    17 StateBlock         : LP-UTF8 stateName
 *                            + u16 stmtCount, { stmt }*
 *                            + u16 transitionCount, { Transition }*
 *    18 Transition         : u8 kind + LP-UTF8 nextStateName
 *                            + u8 hasBindVar(0/1) + LP-UTF8 bindVarName?
 *    19 Terminal           : u8 kind(RETURN/RETURN_VALUE/THROW)
 *                            + u8 hasValue(0/1) + (Expr value)?
 *     3 ExprStmt : Expr(call)
 *     4 VarDecl  : LP-UTF8 name + Expr(initializer)
 *     5 If       : Expr(cond) + u16 thenCount + { stmt }* + u16 elseCount + { stmt }*
 *     6 While    : Expr(cond) + u16 bodyCount + { stmt }*
 *     7 Break    : ∅
 *     8 Continue : ∅
 *     9 Return   : hasValue(u8) + (Expr value)?
 *    10 Try      : u16 tryCount + { stmt }*
 *                  + LP-UTF8 catchName + u16 catchCount + { stmt }*
 *                  + u16 finallyCount + { stmt }*
 *
 * Callback:
 *   kindTag(u8)[0=PENDING,1=SUCCESS,2=FAIL,3=TIMEOUT]
 *   + LP-UTF8 paramName（长度 0 表示 null/无参数）
 *   + u16 stmtCount, { stmt }*
 *
 * action body:
 *   u16 stmtCount, { stmt }*
 * ```
 */
object ExprBlobReader {

    // ---- Expr 标签：与 ExprSerializer 完全一致 ----
    @JvmField val TAG_STRING = 1
    @JvmField val TAG_NUMBER = 2
    @JvmField val TAG_BOOL = 3
    @JvmField val TAG_STATE_REF = 4
    // 编号 5（旧 TAG_CALL）已退役，不复用。
    @JvmField val TAG_INTERP = 6
    @JvmField val TAG_BINARY = 7
    @JvmField val TAG_MEMBER_ACCESS = 8
    @JvmField val TAG_IDENT_REF = 9
    @JvmField val TAG_THEME_REF = 10
    @JvmField val TAG_MEMBER_CALL = 11
    @JvmField val TAG_NULL_LIT = 12
    @JvmField val TAG_UNARY = 13
    @JvmField val TAG_SAFE_MEMBER_ACCESS = 14
    @JvmField val TAG_SAFE_MEMBER_CALL = 15
    @JvmField val TAG_ELVIS = 16
    @JvmField val TAG_TERNARY = 17
    @JvmField val TAG_LOCAL_REF = 18
    @JvmField val TAG_LIST_LITERAL = 19
    @JvmField val TAG_RANGE = 20
    @JvmField val TAG_INDEX = 21
    @JvmField val TAG_WHEN = 22
    @JvmField val TAG_LAMBDA = 23
    @JvmField val TAG_FUN_REF = 24
    @JvmField val TAG_CALL_EXPR = 25
    @JvmField val TAG_BUILTIN_CALL = 26
    @JvmField val TAG_IF_EXPR = 27
    @JvmField val TAG_IS_EXPR = 28

    /**
     * M6-a：Map 字面量 tag。wire 格式：u16 entryCount + {LP-UTF8 key + Expr value}*。
     *
     * 兼容性：IR 版本未上调（仍为 v11）。旧客户端遇到该 tag 会抛
     * IllegalStateException；但旧客户端不会收到含 MapLiteral 的新 blob。
     * 升级到能消费 MapLiteral 的 reader 后即可前向兼容旧 blob。
     */
    @JvmField val TAG_MAP_LITERAL = 29

    /**
     * Stage B：`net.subscribe("ns.topic", handler)` 节点 tag。
     * wire 格式：LP-UTF8 namespace + LP-UTF8 topic + Expr handler。
     * 独立 tag 而非压入 BUILTIN_CALL，原因见 [ExprNode.NetSubscribe] 的 javadoc。
     */
    @JvmField val TAG_NET_SUBSCRIBE = 30

    /** v24：`session.<name>` 表达式引用 tag。wire 格式：LP-UTF8 fieldName。 */
    @JvmField val TAG_SESSION_REF = 31

    /** v24：`persist.<name>` 表达式引用 tag。wire 格式：LP-UTF8 fieldName。 */
    @JvmField val TAG_PERSIST_REF = 32

    /** v25：`derived.<name>` 派生值引用 tag。wire 格式：LP-UTF8 fieldName。 */
    @JvmField val TAG_DERIVED_REF = 33
    @JvmField val TAG_PARAMS_REF = 34

    @JvmField val UNARY_NEG = 0
    @JvmField val UNARY_NOT = 1

    @JvmField val RANGE_CLOSED = 0
    @JvmField val RANGE_HALF_OPEN = 1

    // ---- Interp 片段类型 ----
    @JvmField val INTERP_TEXT = 0
    @JvmField val INTERP_EXPR = 1

    // ---- Stmt 标签 ----
    @JvmField val STMT_ASSIGN = 1
    @JvmField val STMT_REQUEST = 2
    @JvmField val STMT_EXPR_STMT = 3
    @JvmField val STMT_VAR_DECL = 4
    @JvmField val STMT_IF = 5
    @JvmField val STMT_WHILE = 6
    @JvmField val STMT_BREAK = 7
    @JvmField val STMT_CONTINUE = 8
    @JvmField val STMT_RETURN = 9
    @JvmField val STMT_TRY = 10
    @JvmField val STMT_FOR_IN = 11
    @JvmField val STMT_WHEN = 12
    @JvmField val STMT_LOCAL_FUN_DECL = 13
    @JvmField val STMT_NET_REQUEST = 14
    @JvmField val STMT_NET_REQUEST_TIMEOUT = 15

    /** v24：`session.<name> = rhs` 赋值。wire 格式：LP-UTF8 fieldName + Expr(rhs)。 */
    @JvmField val STMT_SESSION_ASSIGN = 20

    /** v24：`persist.<name> = rhs` 赋值。wire 格式：LP-UTF8 fieldName + Expr(rhs)。 */
    @JvmField val STMT_PERSIST_ASSIGN = 21

    /** v25：`batch { stmts }` 合批块。wire 格式：u16 stmtCount + { stmt }*。 */
    @JvmField val STMT_BATCH = 22

    // ===== D2-a 预留：状态机 wire format（IR v8 引入） =====
    @JvmField val STMT_STATE_MACHINE_HEADER = 16
    @JvmField val STMT_STATE_BLOCK = 17
    @JvmField val STMT_TRANSITION = 18
    @JvmField val STMT_TERMINAL = 19

    // ---- Callback 种类 ----
    @JvmField val CALLBACK_PENDING = 0
    @JvmField val CALLBACK_SUCCESS = 1
    @JvmField val CALLBACK_FAIL = 2
    @JvmField val CALLBACK_TIMEOUT = 3

    // ---- LValue 类型 ----
    @JvmField val LV_STATE = 0
    @JvmField val LV_LOCAL = 1

    /** M6-a：索引位置赋值 `target[index] = rhs`。 */
    @JvmField val LV_INDEX = 2

    /** LP-UTF8 字符串最大长度上限（防御外部不可信输入构造超大数组）。 */
    private const val MAX_LP_STRING_LEN = 1 shl 20 // 1 MiB

    /**
     * 表达式 / 语句递归允许的最大嵌套深度，避免恶意 blob 触发栈溢出。
     *
     * 取值 256：真实 .allin 表达式与 action 语句嵌套都在两位数以内，留足余量；
     * 同时远低于默认线程栈在 readExpr/readStmt 单帧开销下的溢出阈值，
     * 可在 StackOverflowError 之前以 IllegalStateException 明确拒绝病态 blob。
     */
    private const val MAX_EXPR_DEPTH = 256

    /**
     * 大端字节游标读取器。
     *
     * 复刻原 Java `DataInputStream(ByteArrayInputStream)` 的读取语义，但避免
     * 依赖 java.io（commonMain 不可用）。所有多字节整数 / double 按大端序读取，
     * 深度计数挂在游标对象上，供 [readExpr] / [readStmt] 两个递归入口增减。
     */
    private class Cursor(val data: ByteArray) {
        var pos: Int = 0
        var depth: Int = 0

        fun available(): Int = data.size - pos

        fun readUnsignedByte(): Int {
            if (pos >= data.size) throw IllegalStateException("字节流读取越界（readUnsignedByte）")
            return data[pos++].toInt() and 0xFF
        }

        fun readUnsignedShort(): Int {
            if (pos + 2 > data.size) throw IllegalStateException("字节流读取越界（readUnsignedShort）")
            val hi = data[pos].toInt() and 0xFF
            val lo = data[pos + 1].toInt() and 0xFF
            pos += 2
            return (hi shl 8) or lo
        }

        fun readDouble(): Double {
            if (pos + 8 > data.size) throw IllegalStateException("字节流读取越界（readDouble）")
            var bits = 0L
            for (i in 0 until 8) {
                bits = (bits shl 8) or (data[pos + i].toLong() and 0xFF)
            }
            pos += 8
            return Double.fromBits(bits)
        }

        fun readFully(len: Int): ByteArray {
            if (pos + len > data.size) throw IllegalStateException("字节流读取越界（readFully len=$len）")
            val out = data.copyOfRange(pos, pos + len)
            pos += len
            return out
        }

        fun enter() {
            if (depth >= MAX_EXPR_DEPTH) {
                throw IllegalStateException(
                    "表达式 / 语句嵌套深度超过限制: " + (depth + 1) + " > " + MAX_EXPR_DEPTH,
                )
            }
            depth++
        }

        fun exit() {
            depth--
        }
    }

    /** 反序列化单个表达式 blob。 */
    @JvmStatic
    fun readExpr(data: ByteArray): ExprNode {
        val cur = Cursor(data)
        val node = readExpr(cur)
        if (cur.available() > 0) {
            throw IllegalStateException("表达式 blob 残留 " + cur.available() + " 字节未消费")
        }
        return node
    }

    /** 反序列化 action body：u16 stmtCount + 依次读取语句。 */
    @JvmStatic
    fun readActionBody(data: ByteArray): List<StmtNode> {
        val cur = Cursor(data)
        val count = cur.readUnsignedShort()
        val stmts = ArrayList<StmtNode>(count)
        for (i in 0 until count) {
            stmts.add(readStmt(cur))
        }
        if (cur.available() > 0) {
            throw IllegalStateException("action body 残留 " + cur.available() + " 字节未消费")
        }
        return stmts
    }

    private fun readExpr(cur: Cursor): ExprNode {
        // 进入一层表达式递归并计数；出口在 finally 中配对递减，确保异常路径不泄漏计数。
        // readExpr / readStmt 是两个唯一的递归入口，所有 tag-reader 都经此分派，
        // 因此只需在这两处计数即可覆盖全部递归路径。
        cur.enter()
        try {
            val tag = cur.readUnsignedByte()
            return when (tag) {
                TAG_STRING -> ExprNode.Literal(readLpString(cur))
                TAG_NUMBER -> ExprNode.Literal(cur.readDouble())
                TAG_BOOL -> ExprNode.Literal(cur.readUnsignedByte() != 0)
                TAG_STATE_REF -> ExprNode.StateRef(readLpString(cur))
                5 -> throw IllegalStateException("tag 5 (旧 Call) 已在 IR v10 退役，不再支持")
                TAG_INTERP -> readInterp(cur)
                TAG_BINARY -> readBinaryOp(cur)
                TAG_MEMBER_ACCESS -> readMemberAccess(cur)
                TAG_IDENT_REF -> ExprNode.IdentRef(readLpString(cur))
                TAG_THEME_REF -> ExprNode.ThemeRef(readLpString(cur))
                TAG_MEMBER_CALL -> readMemberCall(cur)
                TAG_NULL_LIT -> ExprNode.NullLit.INSTANCE
                TAG_UNARY -> readUnaryOp(cur)
                TAG_SAFE_MEMBER_ACCESS -> readSafeMemberAccess(cur)
                TAG_SAFE_MEMBER_CALL -> readSafeMemberCall(cur)
                TAG_ELVIS -> readElvis(cur)
                TAG_TERNARY -> readTernary(cur)
                TAG_LOCAL_REF -> ExprNode.LocalRef(readLpString(cur))
                TAG_LIST_LITERAL -> readListLiteral(cur)
                TAG_RANGE -> readRange(cur)
                TAG_INDEX -> readIndex(cur)
                TAG_WHEN -> readWhen(cur)
                TAG_LAMBDA -> readLambda(cur)
                TAG_FUN_REF -> readFunRef(cur)
                TAG_CALL_EXPR -> readCallExpr(cur)
                TAG_BUILTIN_CALL -> readBuiltinCall(cur)
                TAG_IF_EXPR -> readIfExpr(cur)
                TAG_IS_EXPR -> readIsExpr(cur)
                TAG_MAP_LITERAL -> readMapLiteral(cur)
                TAG_NET_SUBSCRIBE -> readNetSubscribe(cur)
                TAG_SESSION_REF -> ExprNode.SessionRef(readLpString(cur))
                TAG_PERSIST_REF -> ExprNode.PersistRef(readLpString(cur))
                TAG_DERIVED_REF -> ExprNode.DerivedRef(readLpString(cur))
                TAG_PARAMS_REF -> ExprNode.ParamsRef(readLpString(cur))
                else -> throw IllegalStateException("未知表达式 tag: $tag")
            }
        } finally {
            cur.exit()
        }
    }

    // ---- 各 tag 对应的读取方法 ----

    private fun readBinaryOp(cur: Cursor): ExprNode {
        val op = readLpString(cur)
        val left = readExpr(cur)
        val right = readExpr(cur)
        return ExprNode.BinaryOp(op, left, right)
    }

    private fun readMemberAccess(cur: Cursor): ExprNode {
        val target = readExpr(cur)
        val member = readLpString(cur)
        return ExprNode.MemberAccess(target, member)
    }

    private fun readMemberCall(cur: Cursor): ExprNode {
        val callTarget = readExpr(cur)
        val method = readLpString(cur)
        val argCount = cur.readUnsignedShort()
        val args = ArrayList<ExprNode>(argCount)
        for (i in 0 until argCount) {
            args.add(readExpr(cur))
        }
        // Stage 4：命名实参段（向后兼容：u8 hasNamedArgs + 可选 namedArgs 段）
        val hasNamedArgs = cur.readUnsignedByte()
        if (hasNamedArgs == 0) {
            return ExprNode.MemberCall(callTarget, method, args)
        }
        val nn = cur.readUnsignedShort()
        val namedArgs = ArrayList<ExprNode.MemberCall.NamedArg>(nn)
        for (i in 0 until nn) {
            val name = readLpString(cur)
            val value = readExpr(cur)
            namedArgs.add(ExprNode.MemberCall.NamedArg(name, value))
        }
        return ExprNode.MemberCall(callTarget, method, args, namedArgs)
    }

    private fun readUnaryOp(cur: Cursor): ExprNode {
        val sub = cur.readUnsignedByte()
        val unaryOp: String = when (sub) {
            UNARY_NEG -> "-"
            UNARY_NOT -> "!"
            else -> throw IllegalStateException("未知一元子标签: $sub")
        }
        val operand = readExpr(cur)
        return ExprNode.UnaryOp(unaryOp, operand)
    }

    private fun readSafeMemberAccess(cur: Cursor): ExprNode {
        val target = readExpr(cur)
        val member = readLpString(cur)
        return ExprNode.SafeMemberAccess(target, member)
    }

    private fun readSafeMemberCall(cur: Cursor): ExprNode {
        val target = readExpr(cur)
        val method = readLpString(cur)
        val argCount = cur.readUnsignedShort()
        val args = ArrayList<ExprNode>(argCount)
        for (i in 0 until argCount) {
            args.add(readExpr(cur))
        }
        return ExprNode.SafeMemberCall(target, method, args)
    }

    private fun readElvis(cur: Cursor): ExprNode {
        val l = readExpr(cur)
        val r = readExpr(cur)
        return ExprNode.Elvis(l, r)
    }

    private fun readTernary(cur: Cursor): ExprNode {
        val c = readExpr(cur)
        val t = readExpr(cur)
        val e = readExpr(cur)
        return ExprNode.Ternary(c, t, e)
    }

    private fun readIfExpr(cur: Cursor): ExprNode {
        val c = readExpr(cur)
        val thenBody = readStmtList(cur)
        val elseBody = readStmtList(cur)
        return ExprNode.IfExpr(c, thenBody, elseBody)
    }

    private fun readListLiteral(cur: Cursor): ExprNode {
        val n = cur.readUnsignedShort()
        val items = ArrayList<ExprNode>(n)
        for (i in 0 until n) items.add(readExpr(cur))
        return ExprNode.ListLiteral(items)
    }

    private fun readRange(cur: Cursor): ExprNode {
        val rs = readExpr(cur)
        val re = readExpr(cur)
        val k = cur.readUnsignedByte()
        val rk = if (k == RANGE_HALF_OPEN) ExprNode.RangeKind.HALF_OPEN else ExprNode.RangeKind.CLOSED
        return ExprNode.Range(rs, re, rk)
    }

    private fun readIndex(cur: Cursor): ExprNode {
        val t = readExpr(cur)
        val idx = readExpr(cur)
        return ExprNode.Index(t, idx)
    }

    private fun readLambda(cur: Cursor): ExprNode {
        // v9：头部新增 u8 isSuspend，位于 paramCount 之前。
        val isSuspend = cur.readUnsignedByte() != 0
        val pn = cur.readUnsignedShort()
        val params = ArrayList<String>(pn)
        // M5-b：每个形参后追加 u8 hasDefault + Expr?
        val defaults = ArrayList<ExprNode?>(pn)
        var anyDefault = false
        for (i in 0 until pn) {
            params.add(readLpString(cur))
            val hasDefault = cur.readUnsignedByte()
            if (hasDefault != 0) {
                defaults.add(readExpr(cur))
                anyDefault = true
            } else {
                defaults.add(null)
            }
        }
        val body = readStmtList(cur)
        // 全部无默认值时传空列表，保持 equals/hashCode 与旧字节流一致。
        val defaultsForCtor: List<ExprNode?> = if (anyDefault) defaults else emptyList()
        return ExprNode.Lambda(params, body, isSuspend, defaultsForCtor)
    }

    private fun readFunRef(cur: Cursor): ExprNode {
        val funName = readLpString(cur)
        // M6-e/v13：尾部 u8 hasBound + (位置实参段 + 命名实参段)
        val hasBound = cur.readUnsignedByte()
        if (hasBound == 0) {
            return ExprNode.FunRef(funName)
        }
        val boundArgCount = cur.readUnsignedShort()
        val boundArgs = ArrayList<ExprNode>(boundArgCount)
        for (i in 0 until boundArgCount) boundArgs.add(readExpr(cur))
        val boundNamedCount = cur.readUnsignedShort()
        val boundNamed = ArrayList<ExprNode.CallExpr.NamedArg>(boundNamedCount)
        for (i in 0 until boundNamedCount) {
            val n = readLpString(cur)
            val v = readExpr(cur)
            boundNamed.add(ExprNode.CallExpr.NamedArg(n, v))
        }
        return ExprNode.FunRef(funName, boundArgs, boundNamed)
    }

    private fun readCallExpr(cur: Cursor): ExprNode {
        val callee = readExpr(cur)
        // M5-b：在 callee 之后读 u8 hasNamedArgs。
        val hasNamedArgs = cur.readUnsignedByte()
        val an = cur.readUnsignedShort()
        val args = ArrayList<ExprNode>(an)
        for (i in 0 until an) args.add(readExpr(cur))
        if (hasNamedArgs == 0) {
            return ExprNode.CallExpr(callee, args)
        }
        val nn = cur.readUnsignedShort()
        val namedArgs = ArrayList<ExprNode.CallExpr.NamedArg>(nn)
        for (i in 0 until nn) {
            val name = readLpString(cur)
            val value = readExpr(cur)
            namedArgs.add(ExprNode.CallExpr.NamedArg(name, value))
        }
        return ExprNode.CallExpr(callee, args, namedArgs)
    }

    private fun readBuiltinCall(cur: Cursor): ExprNode {
        val name = readLpString(cur)
        val an = cur.readUnsignedShort()
        val args = ArrayList<ExprNode>(an)
        for (i in 0 until an) args.add(readExpr(cur))
        // M6-h Round 2：命名实参 u16 nn + { LP-UTF8 name + Expr value }*
        val nn = cur.readUnsignedShort()
        val namedArgs = ArrayList<ExprNode.BuiltinCall.NamedArg>(nn)
        for (i in 0 until nn) {
            val key = readLpString(cur)
            val value = readExpr(cur)
            namedArgs.add(ExprNode.BuiltinCall.NamedArg(key, value))
        }
        return ExprNode.BuiltinCall(name, args, namedArgs)
    }

    private fun readIsExpr(cur: Cursor): ExprNode {
        val target = readExpr(cur)
        val typeName = readLpString(cur)
        return ExprNode.IsExpr(target, typeName)
    }

    /** Stage B：读取 `net.subscribe("ns.topic", handler)` 节点。wire: ns + topic + handler。 */
    private fun readNetSubscribe(cur: Cursor): ExprNode {
        val namespace = readLpString(cur)
        val topic = readLpString(cur)
        val handler = readExpr(cur)
        return ExprNode.NetSubscribe(namespace, topic, handler)
    }

    private fun readMapLiteral(cur: Cursor): ExprNode {
        // M6-a：Map 字面量 —— u16 entryCount + {LP-UTF8 key + Expr value}*
        val n = cur.readUnsignedShort()
        val entries = ArrayList<ExprNode.MapLiteral.Entry>(n)
        for (i in 0 until n) {
            val key = readLpString(cur)
            val value = readExpr(cur)
            entries.add(ExprNode.MapLiteral.Entry(key, value))
        }
        return ExprNode.MapLiteral(entries)
    }

    /** 读取 when payload；供 ExprNode.When 与 StmtNode.WhenStmt 共享。 */
    private fun readWhen(cur: Cursor): ExprNode.When {
        val subject = readExpr(cur)
        val bn = cur.readUnsignedShort()
        val branches = ArrayList<ExprNode.When.Branch>(bn)
        for (i in 0 until bn) {
            val vn = cur.readUnsignedShort()
            val vs = ArrayList<ExprNode>(vn)
            for (j in 0 until vn) vs.add(readExpr(cur))
            val body = readStmtList(cur)
            branches.add(ExprNode.When.Branch(vs, body))
        }
        val hasElse = cur.readUnsignedByte()
        val elseBody = if (hasElse != 0) readStmtList(cur) else null
        return ExprNode.When(subject, branches, elseBody)
    }

    private fun readInterp(cur: Cursor): ExprNode {
        val partCount = cur.readUnsignedShort()
        val parts = ArrayList<ExprNode.Interp.Part>(partCount)
        for (i in 0 until partCount) {
            val partKind = cur.readUnsignedByte()
            when (partKind) {
                INTERP_TEXT -> parts.add(ExprNode.Interp.TextPart(readLpString(cur)))
                INTERP_EXPR -> parts.add(ExprNode.Interp.ExprPart(readExpr(cur)))
                else -> throw IllegalStateException("未知 interp partKind: $partKind")
            }
        }
        return ExprNode.Interp(parts)
    }

    private fun readStmt(cur: Cursor): StmtNode {
        // 进入一层语句递归并计数；出口在 finally 中配对递减，确保异常路径不泄漏计数。
        cur.enter()
        try {
            return readStmtBody(cur)
        } finally {
            cur.exit()
        }
    }

    private fun readStmtBody(cur: Cursor): StmtNode {
        // 每条语句先读 position（u16 line + u16 column），再读 tag 与 payload。
        val line = cur.readUnsignedShort()
        val column = cur.readUnsignedShort()
        val tag = cur.readUnsignedByte()
        when (tag) {
            STMT_ASSIGN -> return readAssign(cur, line, column)
            STMT_SESSION_ASSIGN -> {
                val fieldName = readLpString(cur)
                val rhs = readExpr(cur)
                return StmtNode.SessionAssign(fieldName, rhs, line, column)
            }
            STMT_PERSIST_ASSIGN -> {
                val fieldName = readLpString(cur)
                val rhs = readExpr(cur)
                return StmtNode.PersistAssign(fieldName, rhs, line, column)
            }
            STMT_REQUEST -> return readRequest(cur, netMode = false, readTimeout = false, line = line, column = column)
            STMT_NET_REQUEST -> return readRequest(cur, netMode = true, readTimeout = false, line = line, column = column)
            STMT_NET_REQUEST_TIMEOUT -> return readRequest(cur, netMode = true, readTimeout = true, line = line, column = column)
            STMT_EXPR_STMT -> return StmtNode.ExprStmt(readExpr(cur), line, column)
            STMT_VAR_DECL -> {
                val name = readLpString(cur)
                val init = readExpr(cur)
                return StmtNode.VarDecl(name, init, line, column)
            }
            STMT_IF -> {
                val cond = readExpr(cur)
                val thenBody = readStmtList(cur)
                val elseBody = readStmtList(cur)
                return StmtNode.If(cond, thenBody, elseBody, line, column)
            }
            STMT_WHILE -> {
                val cond = readExpr(cur)
                val body = readStmtList(cur)
                return StmtNode.While(cond, body, line, column)
            }
            // Break/Continue 为单例，位置信息由接口 default 方法返回 0。
            STMT_BREAK -> return StmtNode.Break.INSTANCE
            STMT_CONTINUE -> return StmtNode.Continue.INSTANCE
            STMT_RETURN -> {
                val has = cur.readUnsignedByte()
                val v = if (has != 0) readExpr(cur) else null
                return StmtNode.Return(v, line, column)
            }
            STMT_TRY -> {
                val tryBody = readStmtList(cur)
                val catchName = readLpString(cur)
                val catchBody = readStmtList(cur)
                val finallyBody = readStmtList(cur)
                return StmtNode.Try(tryBody, catchName, catchBody, finallyBody, line, column)
            }
            STMT_BATCH -> {
                val body = readStmtList(cur)
                return StmtNode.Batch(body, line, column)
            }
            STMT_FOR_IN -> {
                val name = readLpString(cur)
                val iter = readExpr(cur)
                val body = readStmtList(cur)
                return StmtNode.ForIn(name, iter, body, line, column)
            }
            STMT_WHEN -> return StmtNode.WhenStmt(readWhen(cur), line, column)
            STMT_LOCAL_FUN_DECL -> {
                val name = readLpString(cur)
                val pn = cur.readUnsignedShort()
                val params = ArrayList<String>(pn)
                // M5-b：交错读取默认值表达式。
                val defaults = ArrayList<ExprNode?>(pn)
                var anyDefault = false
                for (i in 0 until pn) {
                    params.add(readLpString(cur))
                    val hasDefault = cur.readUnsignedByte()
                    if (hasDefault != 0) {
                        defaults.add(readExpr(cur))
                        anyDefault = true
                    } else {
                        defaults.add(null)
                    }
                }
                val body = readStmtList(cur)
                val defaultsForCtor: List<ExprNode?> = if (anyDefault) defaults else emptyList()
                return StmtNode.LocalFunDecl(name, params, body, defaultsForCtor, line, column)
            }
            STMT_STATE_MACHINE_HEADER -> return readStateMachine(cur, line, column)
            STMT_STATE_BLOCK, STMT_TRANSITION, STMT_TERMINAL ->
                // 这三种 tag 仅作为 STATE_MACHINE_HEADER 内嵌结构出现，独立 stmt 位置上不允许
                throw IllegalStateException(
                    "STATE_BLOCK/TRANSITION/TERMINAL 仅允许嵌在 STATE_MACHINE_HEADER 内（tag=$tag）",
                )
            else -> throw IllegalStateException("未知语句 tag: $tag")
        }
    }

    /** 读取 STMT_STATE_MACHINE_HEADER：D2-b 类型化状态机。 */
    private fun readStateMachine(cur: Cursor, line: Int, column: Int): StmtNode.StateMachine {
        val stateCount = cur.readUnsignedShort()
        val entryStateId = cur.readUnsignedShort()
        val varCount = cur.readUnsignedShort()
        val schemaNames = ArrayList<String>(varCount)
        val schemaTypes = ArrayList<IrType>(varCount)
        for (i in 0 until varCount) {
            schemaNames.add(readLpString(cur))
            schemaTypes.add(IrType.fromOrdinal(cur.readUnsignedByte()))
        }
        val blocks = ArrayList<StmtNode.StateBlock>(stateCount)
        for (i in 0 until stateCount) {
            val blockTag = cur.readUnsignedByte()
            if (blockTag != STMT_STATE_BLOCK) {
                throw IllegalStateException("STATE_MACHINE block 头部 tag 不匹配: $blockTag")
            }
            blocks.add(readStateBlock(cur))
        }
        return StmtNode.StateMachine(schemaNames, schemaTypes, entryStateId, blocks, line, column)
    }

    private fun readStateBlock(cur: Cursor): StmtNode.StateBlock {
        val blockId = cur.readUnsignedShort()
        val stmtCount = cur.readUnsignedShort()
        val stmts = ArrayList<StmtNode>(stmtCount)
        for (i in 0 until stmtCount) {
            stmts.add(readStmt(cur))
        }
        val terminator = cur.readUnsignedByte()
        when (terminator) {
            StmtNode.StateBlock.TERM_TRANSITION -> {
                val trTag = cur.readUnsignedByte()
                if (trTag != STMT_TRANSITION) {
                    throw IllegalStateException("transition 头部 tag 不匹配: $trTag")
                }
                val tr = readTransition(cur)
                return StmtNode.StateBlock(blockId, stmts, terminator, tr, null)
            }
            StmtNode.StateBlock.TERM_NATURAL -> {
                return StmtNode.StateBlock(blockId, stmts, terminator, null, null)
            }
            StmtNode.StateBlock.TERM_RETURN -> {
                val hasValue = cur.readUnsignedByte()
                val v = if (hasValue != 0) readExpr(cur) else null
                return StmtNode.StateBlock(blockId, stmts, terminator, null, v)
            }
            StmtNode.StateBlock.TERM_GOTO -> {
                val nextId = cur.readUnsignedShort()
                return StmtNode.StateBlock(blockId, stmts, terminator, null, null, nextId, null, -1, -1)
            }
            StmtNode.StateBlock.TERM_BRANCH -> {
                val cond = readExpr(cur)
                val thenId = cur.readUnsignedShort()
                val elseId = cur.readUnsignedShort()
                return StmtNode.StateBlock(blockId, stmts, terminator, null, null, -1, cond, thenId, elseId)
            }
            StmtNode.StateBlock.TERM_DELAY -> {
                val delayMs = readExpr(cur)
                val nextId = cur.readUnsignedShort()
                val block = StmtNode.StateBlock(blockId, stmts, terminator, null, null, nextId, null, -1, -1)
                block.delayMs = delayMs
                return block
            }
            else -> throw IllegalStateException("未知 state block terminator: $terminator")
        }
    }

    private fun readTransition(cur: Cursor): StmtNode.Transition {
        // v9：头部新增 u8 transitionKind，0=NetRequest 旧形态，1=SuspendCall。
        val transitionKind = cur.readUnsignedByte()
        return when (transitionKind) {
            StmtNode.Transition.KIND_NET_REQUEST -> readNetRequestTransition(cur)
            StmtNode.Transition.KIND_SUSPEND_CALL -> readSuspendCallTransition(cur)
            else -> throw IllegalStateException("未知 transitionKind: $transitionKind")
        }
    }

    private fun readNetRequestTransition(cur: Cursor): StmtNode.Transition {
        val namespace = readLpString(cur)
        val handler = readLpString(cur)
        val payloadCount = cur.readUnsignedShort()
        val payload = ArrayList<StmtNode.Request.PayloadEntry>(payloadCount)
        for (i in 0 until payloadCount) {
            val key = readLpString(cur)
            val value = readExpr(cur)
            payload.add(StmtNode.Request.PayloadEntry(key, value))
        }
        val hasTimeout = cur.readUnsignedByte()
        val timeoutMs = if (hasTimeout != 0) readExpr(cur) else null
        val successId = cur.readUnsignedShort()
        val failureId = cur.readUnsignedShort()
        val timeoutId = cur.readUnsignedShort()
        val hasBind = cur.readUnsignedByte()
        val bindVarName = if (hasBind != 0) readLpString(cur) else null
        val hasTail = cur.readUnsignedByte()
        val tailExpr = if (hasTail != 0) readExpr(cur) else null
        return StmtNode.Transition(
            namespace, handler, payload, timeoutMs,
            successId, failureId, timeoutId, bindVarName, tailExpr,
        )
    }

    private fun readSuspendCallTransition(cur: Cursor): StmtNode.Transition {
        val lambdaCallee = readExpr(cur)
        val argCount = cur.readUnsignedShort()
        val args = ArrayList<ExprNode>(argCount)
        for (i in 0 until argCount) args.add(readExpr(cur))
        val successId = cur.readUnsignedShort()
        val failureId = cur.readUnsignedShort()
        val timeoutId = cur.readUnsignedShort()
        val hasBind = cur.readUnsignedByte()
        val bindVarName = if (hasBind != 0) readLpString(cur) else null
        // 对称地消费 hasTailExpr 字节（suspend lambda 形态下强制为 0）。
        val hasTail = cur.readUnsignedByte()
        if (hasTail != 0) {
            throw IllegalStateException("suspend-call transition 不允许 tailExpr")
        }
        return StmtNode.Transition(lambdaCallee, args, successId, failureId, timeoutId, bindVarName)
    }

    private fun readStmtList(cur: Cursor): List<StmtNode> {
        val count = cur.readUnsignedShort()
        val out = ArrayList<StmtNode>(count)
        for (i in 0 until count) {
            out.add(readStmt(cur))
        }
        return out
    }

    private fun readAssign(cur: Cursor, line: Int, column: Int): StmtNode {
        val lvKind = cur.readUnsignedByte()
        if (lvKind == LV_STATE) {
            val name = readLpString(cur)
            val rhs = readExpr(cur)
            return StmtNode.Assign(name, rhs, line, column)
        }
        if (lvKind == LV_LOCAL) {
            val name = readLpString(cur)
            val rhs = readExpr(cur)
            return StmtNode.LocalAssign(name, rhs, line, column)
        }
        if (lvKind == LV_INDEX) {
            // M6-a：wire 形态 —— Expr(target) + Expr(index) + Expr(rhs)
            val target = readExpr(cur)
            val index = readExpr(cur)
            val rhs = readExpr(cur)
            return StmtNode.IndexAssign(target, index, rhs, line, column)
        }
        throw IllegalStateException("未知 lvalueKind: $lvKind")
    }

    private fun readRequest(cur: Cursor, netMode: Boolean, readTimeout: Boolean, line: Int, column: Int): StmtNode {
        val namespace = readLpString(cur)
        val handler = readLpString(cur)

        val payloadCount = cur.readUnsignedShort()
        val payload = ArrayList<StmtNode.Request.PayloadEntry>(payloadCount)
        for (i in 0 until payloadCount) {
            val key = readLpString(cur)
            val value = readExpr(cur)
            payload.add(StmtNode.Request.PayloadEntry(key, value))
        }

        val callbackCount = cur.readUnsignedByte()
        val callbacks = ArrayList<StmtNode.Request.Callback>(callbackCount)
        for (i in 0 until callbackCount) {
            callbacks.add(readCallback(cur))
        }
        val timeoutMs = if (readTimeout) readExpr(cur) else null
        return StmtNode.Request(namespace, handler, payload, callbacks, netMode, timeoutMs, line, column)
    }

    private fun readCallback(cur: Cursor): StmtNode.Request.Callback {
        val kindTag = cur.readUnsignedByte()
        val kind: StmtNode.Request.Callback.Kind = when (kindTag) {
            CALLBACK_PENDING -> StmtNode.Request.Callback.Kind.PENDING
            CALLBACK_SUCCESS -> StmtNode.Request.Callback.Kind.SUCCESS
            CALLBACK_FAIL -> StmtNode.Request.Callback.Kind.FAIL
            CALLBACK_TIMEOUT -> StmtNode.Request.Callback.Kind.TIMEOUT
            else -> throw IllegalStateException("未知 callback kind: $kindTag")
        }
        val rawParam = readLpString(cur)
        // 空字符串约定为 null（pending/timeout 无参数）
        val paramName = if (rawParam.isEmpty()) null else rawParam

        val stmtCount = cur.readUnsignedShort()
        val body = ArrayList<StmtNode>(stmtCount)
        for (i in 0 until stmtCount) {
            body.add(readStmt(cur))
        }
        return StmtNode.Request.Callback(kind, paramName, body)
    }

    private fun readLpString(cur: Cursor): String {
        val len = cur.readUnsignedShort()
        if (len > MAX_LP_STRING_LEN) {
            throw IllegalStateException("LP-UTF8 长度超限: $len")
        }
        val bytes = cur.readFully(len)
        return bytes.decodeToString()
    }
}
